// Slim dispatcher that picks the right organism based on Message.type.
//
// Sprint UI-4 — all per-type rendering logic has been extracted to
// organisms/ (AssistantMessage, UserMessage, SystemPill, ErrorCard).
// This file remains as the single entry point consumed by
// ConversationList and SubagentPanel.
//
// Sprint sticky-message-section-stacking-cursor-parity — the `pinAtTop`
// prop and the StickyMessage import are gone. User-message sticky
// behaviour is now driven entirely by the per-turn `<section>` in
// ConversationList + `position: sticky` on UserMessage itself.

import type { Message } from '../types';
import {
  asToolUse,
  asToolResult,
  asUserContent,
  toolResultText,
  SUBAGENT_TOOL_NAMES,
} from '../lib/messages';
import { useTranslation } from '../i18n';
import { AssistantMessage } from './organisms/AssistantMessage';
import { UserMessage } from './organisms/UserMessage';
import { SystemPill } from './organisms/SystemPill';
import { ErrorCard } from './organisms/ErrorCard';
import { ToolCallRow } from './organisms/ToolCallRow';
import { TodoChecklist } from './TodoChecklist';
// Sprint ai-elements-rendering (spec §3.4) — persisted `thinking_block`
// messages render again as a Reasoning-style duration line ("Thought for
// Xs"), upgrading Sprint 17's render-null. The persisted content carries
// timings only (`ThinkingBlockContent` — the reasoning text is redacted
// on Claude Max), so there is no expandable body; the line is static.
import { TodoUpdatePills } from './founder/TodoUpdatePill';
import { DeveloperOnly } from './founder/DeveloperOnly';
import { Icon } from './atoms/Icon';
import type { ThinkingBlockContent } from '../types';

export interface MessageBubbleProps {
  message: Message;
  toolResultMessage?: Message;
  todoUpdatePills?: import('../lib/tool-rendering/diff-todos').TodoUpdatePill[];
  showModelBadge?: boolean;
  onEditMessage?: (text: string) => void;
  onResendMessage?: (text: string) => void;
  // Sprint attachments-anthropic-parity Phase D — forwarded to `UserMessage`
  // so persisted attachment chips can fetch their thumbnail from
  // `/api/agents/:agentId/attachments/:chipId/raw`. Optional because some
  // surfaces (subagent panel, archived agents) render without an active
  // agent id; chips without an id degrade to the generic icon.
  agentId?: string;
}

export function MessageBubble({
  message,
  toolResultMessage,
  showModelBadge,
  todoUpdatePills,
  onEditMessage,
  onResendMessage,
  agentId,
}: MessageBubbleProps) {
  switch (message.type) {
    case 'assistant_text':
      return (
        <AssistantMessage
          message={message}
          showModelBadge={showModelBadge}
        />
      );

    case 'user': {
      const text = asUserContent(message).text;
      return (
        <UserMessage
          text={text}
          onEdit={onEditMessage}
          onResend={onResendMessage}
          attachments={message.attachments ?? null}
          agentId={agentId}
        />
      );
    }

    case 'tool_use': {
      const tu = asToolUse(message);
      if (tu.name === 'TodoWrite') {
        return (
          <div data-testid="todo-write-block">
            {todoUpdatePills && todoUpdatePills.length > 0 && (
              <TodoUpdatePills pills={todoUpdatePills} />
            )}
            <TodoChecklist message={message} />
          </div>
        );
      }
      if (SUBAGENT_TOOL_NAMES.has(tu.name)) {
        return (
          <ToolCallRow
            toolUseMessage={message}
            toolResultMessage={toolResultMessage}
          />
        );
      }
      return (
        <ToolCallRow
          toolUseMessage={message}
          toolResultMessage={toolResultMessage}
        />
      );
    }

    case 'tool_result':
      return <OrphanToolResult message={message} />;

    case 'system':
      return <SystemPill message={message} />;

    case 'error':
      return <ErrorCard message={message} />;

    case 'thinking_block':
      return <ThinkingDurationLine message={message} />;

    default:
      return null;
  }
}

// Visual sibling of `ReasoningTrigger` (ai-elements/reasoning.tsx) minus
// the chevron/collapse affordance — there is no persisted thinking text to
// reveal, only the duration.
function ThinkingDurationLine({ message }: { message: Message }) {
  const { t } = useTranslation();
  const c = message.content as Partial<ThinkingBlockContent>;
  const durationSec =
    typeof c.durationMs === 'number' && Number.isFinite(c.durationMs) && c.durationMs > 0
      ? Math.max(1, Math.round(c.durationMs / 1000))
      : undefined;
  return (
    <div
      className="flex items-center gap-2 text-body text-muted-foreground"
      data-testid="thinking-block"
    >
      <Icon name="Brain" size={14} className="shrink-0" aria-hidden="true" />
      <span>
        {durationSec === undefined
          ? t('aiElements.reasoning.thoughtBriefly')
          : t('aiElements.reasoning.thoughtForSeconds', { duration: durationSec })}
      </span>
    </div>
  );
}

function OrphanToolResult({ message }: { message: Message }) {
  const { t } = useTranslation();
  const tr = asToolResult(message);
  const text = toolResultText(tr.content);
  // Sprint messages-section-fixes (D-msg-12) — orphan tool results are
  // a sign of upstream protocol breakage (a tool_result whose tool_use
  // never landed). The founder cannot act on it; only a developer
  // debugging the protocol can. Wrapped in <DeveloperOnly> so founder
  // mode hides it entirely.
  return (
    <DeveloperOnly>
      <div
        className={`rounded-lg border px-3 py-2 text-caption whitespace-pre-wrap break-words ${
          tr.is_error
            ? 'border-status-error/40 bg-status-error/10 text-status-error'
            : 'border-border bg-background/50 text-foreground'
        }`}
        data-testid="orphan-tool-result"
      >
        <div className="text-caption uppercase tracking-wide text-muted-foreground/70 mb-1">
          {tr.is_error ? t('messageBubble.toolError') : t('messageBubble.toolResult')}
        </div>
        {text || <span className="text-muted-foreground/70">{t('messageBubble.empty')}</span>}
      </div>
    </DeveloperOnly>
  );
}
